import { EraQL, Expression, Ty } from '@cotera/era';
import { err, ok, Result } from 'neverthrow';
import { GraphError, InterNodeLocation } from './types';

export type ParseCtxFor = (
  source: InterNodeLocation,
  opts: {
    readonly bindings: EraQL.Bindings;
    readonly attributes: EraQL.ParseCtx['attributes'];
  }
) => EraQL.ParseCtx;

export const mkEraqlExprChecker =
  (ctxFor: ParseCtxFor) =>
  (
    loc: InterNodeLocation,
    eraql: string,
    opts: {
      attributes: EraQL.ParseCtx['attributes'];
      disallowAggregate: boolean;
      disallowWindow: boolean;
      bindings?: EraQL.Bindings;
      reqTy?: Ty.Shorthand[];
    }
  ): Result<Expression, [loc: InterNodeLocation, err: GraphError][]> => {
    const exprRes = EraQL.parseExpr(
      eraql,
      ctxFor(loc, {
        bindings: opts.bindings ?? {},
        attributes: opts.attributes,
      })
    );

    if (exprRes.isErr()) {
      const e: [loc: InterNodeLocation, err: GraphError] = [
        loc,
        { t: 'eraql-errors', errs: exprRes.error, source: eraql },
      ];
      return err([e]);
    }

    const expr = exprRes.value;

    const errs: GraphError[] = [];

    if (opts.disallowWindow && expr.isWindowed) {
      errs.push({ t: 'cant-be-windowed' });
    }

    if (opts.disallowAggregate && expr.isAggregated) {
      errs.push({ t: 'cant-be-aggregated' });
    }

    if (
      opts.reqTy !== undefined &&
      !opts.reqTy.some((ty) => expr.implements(ty))
    ) {
      errs.push({
        t: 'invalid-type',
        wanted: opts.reqTy.map((ty) => Ty.ty(ty)),
        got: expr.ty,
      });
    }

    return errs.length > 0 ? err(errs.map((e) => [loc, e])) : ok(expr);
  };
